import React, {useEffect} from 'react'
import {useDispatch} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {
  ProposalAuthProvider,
  useProposalAuth,
  useProposalAuthClient,
} from './contexts/proposal-auth-context'
import {useProposalLink} from './hooks/use-proposal-link'
import {ProposalOtpVerification} from './proposal-otp-verification'
import {ClientProposalView} from '../ClientProposalView'
import {
  Container,
  NavBar,
  NavLeft,
  PageWrap,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalBody,
} from '../ClientProposalView/styled.components'
import {PTKeapWhite, PTLogoWhite} from '../../../../assets'
import {
  useEnsureProposalInfo,
  isScheduleCallRequired,
} from '../../../../utils/client-proposal.util'
import {
  setSchdulerRequirementStatus,
  updateProposalInfo,
} from '../../../../app-store/ProposalSlice'

const BLOCKED_STATUS_CONFIG = {
  EXPIRED: {
    title: 'Proposal Expired',
    subtitle:
      'This proposal has expired and is no longer available. Please contact your advisor to receive an updated proposal.',
  },
  COMPLETED: {
    title: 'Proposal Completed',
    subtitle:
      'This proposal has already been completed. Please contact your advisor if you have any questions.',
  },
  DECLINED: {
    title: 'Proposal Declined',
    subtitle:
      'This proposal has been declined. Please contact your advisor if you would like to revisit your options.',
  },
}

function BlockedProposalModal({
  statusCode,
  proposalId,
  expirationDate,
  client,
  dispatch,
}) {
  const isExpiredByDate =
    !!expirationDate && new Date(expirationDate) < new Date()

  useEffect(() => {
    if (!isExpiredByDate || statusCode === 'EXPIRED' || !proposalId || !client)
      return
    async function autoExpire() {
      try {
        await client(`proposals/${proposalId}/updateStatus`, {
          data: {action: 'EXPIRED'},
          method: 'POST',
        })
        dispatch(updateProposalInfo({statusCode: 'EXPIRED'}))
      } catch {
        // silently fail — the modal still blocks the user
      }
    }
    autoExpire()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveStatus = isExpiredByDate ? 'EXPIRED' : statusCode
  const config = BLOCKED_STATUS_CONFIG[effectiveStatus]
  if (!config) {
    return null
  }

  return (
    <PageWrap>
      <Container>
        <NavBar>
          <NavLeft>
            <img
              src={PTLogoWhite}
              alt="Thryv"
              style={{height: 30, objectFit: 'contain'}}
            />
            <span></span>
            <img
              src={PTKeapWhite}
              alt="Keap"
              style={{height: 30, objectFit: 'contain'}}
            />
          </NavLeft>
        </NavBar>
        <ModalOverlay>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{config.title}</ModalTitle>
              <ModalSubtitle>{config.subtitle}</ModalSubtitle>
            </ModalHeader>
            <ModalBody />
          </ModalCard>
        </ModalOverlay>
      </Container>
    </PageWrap>
  )
}

function ProposalGate() {
  const {randomId} = useProposalLink()
  const {isVerified, mfaRequired, authToken, checkStatus} = useProposalAuth()
  const client = useProposalAuthClient()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    proposalInfo,
    selectedOptionId,
    loading: proposalInfoLoading,
  } = useEnsureProposalInfo({
    proposalId: randomId,
    client,
    enabled: Boolean(authToken),
  })
  useEffect(() => {
    if (randomId) {
      checkStatus(randomId)
    }
  }, [randomId, checkStatus])

  // Still checking status
  if (mfaRequired === null) {
    return null
  }

  // Token is ready and the proposal fetch it unblocked is still in flight —
  // wait so ClientProposalView mounts with proposalInfo already in Redux,
  // instead of firing its own duplicate proposals/{id} fetch.
  if (authToken && proposalInfoLoading) {
    return null
  }

  const isExpiredByDate =
    !!proposalInfo.expirationDate &&
    new Date(proposalInfo.expirationDate) < new Date()

  const isBlockedStatus =
    proposalInfo.statusCode === 'EXPIRED' ||
    proposalInfo.statusCode === 'COMPLETED' ||
    proposalInfo.statusCode === 'DECLINED' ||
    isExpiredByDate

  const blockedModalProps = {
    statusCode: proposalInfo.statusCode,
    proposalId: proposalInfo.proposalId,
    expirationDate: proposalInfo.expirationDate,
    client,
    dispatch,
  }

  // Non-MFA path: show proposal view once scoped token is available
  if (!mfaRequired) {
    if (authToken) {
      if (isBlockedStatus) {
        return <BlockedProposalModal {...blockedModalProps} />
      }
      if (proposalInfo.statusCode === 'ACCEPTED') {
        const isScheduleRequired = isScheduleCallRequired(proposalInfo)
        dispatch(setSchdulerRequirementStatus(isScheduleRequired))

        navigate(
          `/proposal-view/${randomId}/${
            isScheduleRequired
              ? proposalInfo?.thryvId
                ? 'schedule-appointment'
                : 'account-information'
              : 'client-payment'
          }`,
        )
        return null
      } else {
        return <ClientProposalView proposalId={randomId} />
      }
    } else {
      return null
    }
  }

  // MFA path: show proposal view after OTP verification
  if (isVerified && authToken) {
    if (isBlockedStatus) {
      return <BlockedProposalModal {...blockedModalProps} />
    }
    if (proposalInfo.statusCode === 'ACCEPTED') {
      const isScheduleRequired = isScheduleCallRequired(
        proposalInfo,
        selectedOptionId,
      )
      dispatch(setSchdulerRequirementStatus(isScheduleRequired))
      navigate(
        `/proposal-view/${randomId}/${
          isScheduleRequired
            ? proposalInfo?.thryvId
              ? 'schedule-appointment'
              : 'account-information'
            : 'client-payment'
        }`,
      )
      return null
    }
    return <ClientProposalView proposalId={randomId} />
  }

  // Pre-verification MFA path: block OTP form if status already known
  if (isBlockedStatus) {
    return <BlockedProposalModal {...blockedModalProps} />
  }

  return (
    <PageWrap>
      <Container>
        <NavBar>
          <NavLeft>
            <img
              src={PTLogoWhite}
              alt="Thryv"
              style={{height: 30, objectFit: 'contain'}}
            />
            <span></span>
            <img
              src={PTKeapWhite}
              alt="Keap"
              style={{height: 30, objectFit: 'contain'}}
            />
          </NavLeft>
        </NavBar>
        <ProposalOtpVerification />
      </Container>
    </PageWrap>
  )
}

export function ClientProposalViewAuth() {
  return (
    <ProposalAuthProvider>
      <ProposalGate />
    </ProposalAuthProvider>
  )
}

export {ProposalAuthProvider} from './contexts/proposal-auth-context'
export {
  useProposalAuth,
  useProposalAuthClient,
} from './contexts/proposal-auth-context'
export {useProposalLink} from './hooks/use-proposal-link'
